<template>
  <div class="page">
    <div class="topbar">
      <button class="btn" @click="$router.push('/admin/imports')">← Back to Imports</button>
      <h2>Import Roster</h2>
    </div>
    <div v-if="notices && notices.length" class="notices">
      <div v-for="n in notices" :key="n.id" class="notice" :class="n.type">
        <span>{{ n.text }}</span>
        <button class="close" @click="dismissNotice(n.id)">×</button>
      </div>
    </div>
    <div class="card status">
      <div class="status-row">
        <span class="pill" :class="statusClass">{{ statusText }}</span>
        <span v-if="meta">File: <strong>{{ meta.file_name || meta.filename }}</strong></span>
        <span v-if="meta">Created: {{ fmt(meta.created_at) }}</span>
        <span v-if="meta && meta.updated_at">Updated: {{ fmt(meta.updated_at) }}</span>
        <span class="next" v-if="nextAction"><strong>Next:</strong> {{ nextAction }}</span>
      </div>
      <div class="tabs">
        <button class="tab" :class="{active: currentTab==='setup', disabled: isBusy || isStaged}" @click="goTab('setup')">Setup</button>
        <button class="tab" :class="{active: currentTab==='configure', disabled: !canGoConfigure || isBusy || isStaged}" @click="goTab('configure')">Configure</button>
        <button class="tab" :class="{active: currentTab==='preview', disabled: !canGoPreview || isBusy}" @click="goTab('preview')">Preview</button>
        <button class="tab" :class="{active: currentTab==='run', disabled: !canGoRun || isBusy}" @click="goTab('run')">Run</button>
      </div>
      <div class="metrics">
        <div class="metric" v-if="report">
          <span class="label">Rows</span>
          <span class="value">{{ report.total_rows || 0 }}</span>
        </div>
        <div class="metric" v-if="report">
          <span class="label">Errors</span>
          <span class="value" :class="{ bad: (report.error_count||0) > 0 }">{{ report.error_count || 0 }}</span>
        </div>
        <div class="metric" v-if="downloads && downloads.length">
          <span class="label">Outputs</span>
          <span class="value">{{ downloads.length }}</span>
        </div>
      </div>
    </div>
    <div v-if="currentTab==='preview' && report && (report.error_count||0) > 0" class="card rounded-xl p-5 bg-white border">
      <div class="font-medium mb-1">{{ report.error_count }} validation errors</div>
      <ul class="list-inside list-disc text-sm">
        <li v-for="e in groupedErrors.slice(0,10)" :key="e._k">
          {{ e.sheet }} row {{ e.row }} — {{ e.error }}
          <span class="muted" :title="errorExplanation(e.error)">ⓘ</span>
        </li>
      </ul>
      <div class="text-xs muted" v-if="groupedErrors.length > 10">Showing first 10.</div>
    </div>
    <div v-if="currentTab==='setup'" class="card rounded-xl p-5 bg-white border">
      <ContextHeader :university-name="selectedUniversityName" :ceremony-text="selectedCeremonyLabel" />
      <SectionCard title="Import Context" helper="This import will add graduates to the selected ceremony.">
        <div class="row">
          <label class="block text-sm font-medium text-gray-700 mb-1">University</label>
          <div class="text-gray-900">{{ selectedUniversityName || '—' }}</div>
        </div>
        <div class="row">
          <label class="block text-sm font-medium text-gray-700 mb-1">Ceremony</label>
          <div class="ceremony-row">
            <select v-model="selectedCeremonyId" :disabled="ceremoniesLoading || ceremonies.length === 0 || isStaged" class="px-3 py-2 border border-gray-300 rounded-md bg-white">
              <option value="">Select ceremony…</option>
              <option v-for="c in ceremonies" :key="c.id || c.ceremony_id" :value="c.id || c.ceremony_id">{{ formatCeremony(c) }}</option>
            </select>
            <span v-if="ceremoniesLoading" class="muted">Loading ceremonies…</span>
            <span v-else-if="!ceremonies.length" class="muted">No ceremonies found for this university</span>

          </div>
        </div>
      </SectionCard>
      <SectionCard title="File Upload">
        <div class="upload">
          <input ref="fileInput" type="file" accept=".xlsx" @change="onFile" class="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border file:border-gray-300 file:text-sm file:font-medium file:bg-white file:text-gray-700 hover:file:bg-gray-50" />
          <div v-if="importId && !isStaged" class="mt-2 text-sm text-gray-700">
            <button class="px-2 py-1 rounded-md border bg-white hover:bg-gray-50" @click="triggerFile">Replace file</button>
            <span class="ml-2 muted">Selecting a new file creates a new import.</span>
          </div>
          <small class="text-gray-600">.xlsx only. Max 25MB.</small>
        </div>
        <div v-if="uploading" class="inline-status"><span class="spinner sm"></span> Uploading…</div>
        <div class="muted" v-if="importId">Import ID: <code>{{ importId }}</code></div>
        <div v-if="sheets && sheets.length" class="muted">Sheets: {{ sheets.length }}</div>
      </SectionCard>
    </div>
    <div v-if="currentTab==='configure'" class="card rounded-xl p-5 bg-white border">
      <ContextHeader :university-name="selectedUniversityName" :ceremony-text="selectedCeremonyLabel" />
      <h3 class="text-lg font-semibold text-gray-900 mb-2">Match your columns</h3>
      <div class="config-grid">
        <div class="left">
          <div class="row" v-if="sheets.length > 1">
            <label class="block text-sm font-medium text-gray-700 mb-1">Excel sheet</label>
            <select v-model="selectedSheet" @change="onSheetChange" class="px-3 py-2 border border-gray-300 rounded-md bg-white">
              <option disabled value="">Select a sheet…</option>
              <option v-for="s in sheets" :key="s.name" :value="s.name">{{ s.name }}</option>
            </select>
          </div>
          <div class="preview" v-if="activeSheet">
            <h4 class="font-medium text-gray-900 mb-1">Data in your file</h4>
            <table class="table small">
              <thead>
                <tr>
                  <th v-for="h in activeSheet.headers" :key="h">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row,ridx) in activeSheet.sample_rows" :key="ridx">
                  <td v-for="(cell,cidx) in row" :key="cidx">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="right">
          <div class="map-panel rounded-lg p-4 bg-gray-50 border">
            <div class="helper text-sm text-gray-700">Match your Excel columns to our required fields.</div>
            <div class="row">
              <label class="block text-sm font-medium text-gray-700 mb-1">Filter columns</label>
              <input type="text" v-model="filterText" placeholder="Type to filter headers…" :disabled="isStaged" class="px-3 py-2 border border-gray-300 rounded-md w-full" />
            </div>
            <div class="row mode-row">
              <span class="muted">Mode:</span>
              <span v-if="usingFullName">Full Name</span>
              <span v-else-if="usingFirstLast">First + Last</span>
              <span v-else>Not set</span>
              <a v-if="usingFullName && !isStaged" class="link text-blue-700" @click.prevent="switchToFirstLast">Switch to First + Last</a>
              <a v-else-if="usingFirstLast && !isStaged" class="link text-blue-700" @click.prevent="switchToFullName">Switch to Full Name</a>
              <a v-if="!isStaged" class="link text-blue-700" @click.prevent="clearMapping">Clear</a>
            </div>
            <div class="map-grid">
              <div class="col">
                <label class="block text-sm font-medium text-gray-700 mb-1">Full Name (or use First+Last)</label>
                <select v-model="mapUI.full_name" :disabled="(mapUI.first_name && mapUI.last_name) || isStaged" class="px-3 py-2 border border-gray-300 rounded-md bg-white w-full">
                  <option value="">—</option>
                  <option v-for="h in filteredHeaders" :key="h" :value="h">{{ h }}</option>
                </select>
              </div>
              <div class="col">
                <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <select v-model="mapUI.first_name" :disabled="!!mapUI.full_name || isStaged" class="px-3 py-2 border border-gray-300 rounded-md bg-white w-full">
                  <option value="">—</option>
                  <option v-for="h in filteredHeaders" :key="h" :value="h">{{ h }}</option>
                </select>
              </div>
              <div class="col">
                <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <select v-model="mapUI.last_name" :disabled="!!mapUI.full_name || isStaged" class="px-3 py-2 border border-gray-300 rounded-md bg-white w-full">
                  <option value="">—</option>
                  <option v-for="h in filteredHeaders" :key="h" :value="h">{{ h }}</option>
                </select>
              </div>
              <div class="col">
                <label class="block text-sm font-medium text-gray-700 mb-1">Email (all rows or none)</label>
                <select v-model="mapUI.email" :disabled="isStaged" class="px-3 py-2 border border-gray-300 rounded-md bg-white w-full">
                  <option value="">—</option>
                  <option v-for="h in filteredHeaders" :key="h" :value="h">{{ h }}</option>
                </select>
              </div>
              <div class="col" style="grid-column: 1/-1;" v-if="optionalFields && optionalFields.length">
                <label class="block text-sm font-medium text-gray-700 mb-1">Optional fields</label>
                <div class="grid" style="grid-template-columns: repeat(2, minmax(0,1fr)); gap:8px;">
                  <div v-for="f in optionalFields" :key="f.name || f.key">
                    <label class="block text-xs text-gray-600 mb-0.5">
                      {{ f.label || f.title || f.name || f.key }}
                      <span v-if="f.primary || f.secondary" class="text-red-600">*</span>
                    </label>
                    <select v-model="mapOptional[f.name || f.key]" :disabled="isStaged" class="px-3 py-2 border border-gray-300 rounded-md bg-white w-full">
                      <option value="">—</option>
                      <option v-for="h in filteredHeaders" :key="h" :value="h">{{ h }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="row" v-if="missingRequiredFields.length">
              <span class="bad-chip">Missing required fields: {{ missingRequiredFields.join(', ') }}</span>
            </div>
            <div class="row" v-else-if="mappingValid">
              <span class="ok-chip">✓ Required fields matched</span>
            </div>
            <div class="row">
              <button class="btn" @click="suggest" :disabled="suggesting || isStaged">
                <span v-if="suggesting" class="spinner sm"></span>
                {{ suggesting ? 'Matching…' : 'Match columns automatically' }}
              </button>
            </div>
            <div class="row">
              <label><input type="checkbox" v-model="showAdvanced" :disabled="isStaged" /> Advanced: edit JSON mapping</label>
            </div>
            <textarea v-if="showAdvanced" v-model="mappingText" class="json" :disabled="isStaged"></textarea>
            <div class="row">
              <button class="btn primary" @click="saveMapping" :disabled="isStaged || savingMapping || !selectedSheet || !selectedCeremonyId || !mappingValid">
                <span v-if="savingMapping" class="spinner sm"></span>
                {{ savingMapping ? 'Saving…' : 'Save column matching' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="currentTab==='preview' && importId" class="card rounded-xl p-5 bg-white border">
      <div class="section-header">
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Preview</h3>
        <button v-if="hasMappingSaved && !isStaged" class="btn" @click="downloadMapped" :disabled="downloadingMapped || !selectedSheet">
          <span v-if="downloadingMapped" class="spinner sm"></span>
          {{ downloadingMapped ? 'Preparing…' : 'Download mapped Excel' }}
        </button>
      </div>
      <div class="row" v-if="!selectedSheet">
        <div class="muted">Select an Excel sheet to see a preview.</div>
      </div>
      <div class="row" v-else>
        <div v-if="loadingPreview" class="inline-status"><span class="spinner sm"></span> Loading preview…</div>
        <div v-else>
          <div v-if="previewHasErrors && !isStaged" class="rounded-md p-3 bg-red-50 border border-red-200 text-red-800 mb-2">
            Some rows are missing required fields or email. Hover highlighted cells for details.
            <button v-if="!isStaged" class="ml-2 px-2 py-1 rounded border bg-white hover:bg-gray-50 text-sm" @click="goTab('configure')">Adjust mapping</button>
          </div>
          <div v-if="previewModeNote" class="mb-2 text-sm text-amber-700">{{ previewModeNote }}</div>
          <div class="preview">
            <table v-if="preview.rows && preview.rows.length" class="table small">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Full Name</th>
                  <th>Ceremony</th>
                  <th v-if="preview.rows && preview.rows.length && 'email' in preview.rows[0]">Email</th>
                  <th v-for="k in previewExtraKeys" :key="k">{{ k }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in preview.rows" :key="r.row_index">
                  <td>{{ r.row_index }}</td>
                  <td :class="{bad: r._missing_full_name}" :title="r._missing_full_name ? 'Full name is required. Choose a Full Name column or First + Last in Match your columns.' : ''">{{ r.full_name || '—' }}</td>
                  <td :class="{bad: r._missing_ceremony}" :title="r._missing_ceremony ? 'Ceremony is required. Select a ceremony on Setup.' : ''">{{ r.ceremony || '—' }}</td>
                  <td v-if="'email' in r" :class="{bad: r._missing_email}" :title="r._missing_email ? 'Email is required for all rows when mapped.' : ''">{{ r.email || '—' }}</td>
                  <td v-for="k in previewExtraKeys" :key="k" :class="{bad: r[`_missing_${k}`]}" :title="r[`_missing_${k}`] ? `${k} is required.` : ''">{{ r[k] }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="muted">No preview rows.</div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="currentTab==='run'" class="card rounded-xl p-5 bg-white border">
      <ContextHeader :university-name="selectedUniversityName" :ceremony-text="selectedCeremonyLabel" />
      <h3 class="text-lg font-semibold text-gray-900 mb-2">Import & Status</h3>
      <div class="row">
        <button class="btn primary" @click="importAndRun" :disabled="running || isStaged"><span v-if="running" class="spinner sm"></span> {{ isStaged ? 'Imported' : (running ? 'Running…' : 'Import graduates') }}</button>
      </div>
      <div class="row" v-if="running">
        <div class="rounded-md p-3 bg-gray-50 border text-gray-800">
          <div class="font-medium mb-1">Import progress</div>
          <ul class="space-y-1 text-sm">
            <li class="flex items-center gap-2">
              <span v-if="runSteps.save==='in_progress'" class="spinner sm"></span>
              <span v-else-if="runSteps.save==='done'" class="ok-chip">✓</span>
              <span v-else-if="runSteps.save==='error'" class="bad">!</span>
              <span v-else class="muted">•</span>
              Save column matching
            </li>
            <li class="flex items-center gap-2">
              <span v-if="runSteps.validate==='in_progress'" class="spinner sm"></span>
              <span v-else-if="runSteps.validate==='done'" class="ok-chip">✓</span>
              <span v-else-if="runSteps.validate==='error'" class="bad">!</span>
              <span v-else class="muted">•</span>
              Validate data
            </li>
            <li class="flex items-center gap-2">
              <span v-if="runSteps.import==='in_progress'" class="spinner sm"></span>
              <span v-else-if="runSteps.import==='done'" class="ok-chip">✓</span>
              <span v-else class="muted">•</span>
              Import and stage
            </li>
          </ul>
        </div>
      </div>
      <div class="row" v-if="isStaged">
        <div class="rounded-md p-3 bg-blue-50 text-blue-900 border border-blue-200">
          Imported successfully. We’ll email you when outputs are ready — typically 24–48 hours after the ceremony, provided everything has gone to plan.
        </div>
      </div>
      <SectionCard v-if="isStaged" title="Staged files" helper="Download the staged data per ceremony.">
        <div v-if="loadingStaged" class="muted">Loading staged files…</div>
        <div v-else-if="!stagedDisplay.length" class="muted">No staged files yet.</div>
        <ul v-else class="space-y-2">
          <li v-for="item in stagedDisplay" :key="item.key" class="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2">
            <div>
              <div class="text-sm font-medium text-gray-900">{{ stagedLabel(item) }}</div>
              <div v-if="item.size" class="text-xs text-gray-500">{{ formatBytes(item.size) }}</div>
            </div>
            <a class="btn" :href="item.url" target="_blank" rel="noopener">Download</a>
          </li>
        </ul>
      </SectionCard>
      <SectionCard v-if="showDownloads" title="Outputs" helper="Enriched output files will appear here once processing completes.">
        <div class="row">
          <button class="btn" @click="loadDownloads()" :disabled="loadingDownloads">
            <span v-if="loadingDownloads" class="spinner sm"></span>
            {{ loadingDownloads ? 'Refreshing…' : 'Refresh outputs' }}
          </button>
          <span v-if="polling.downloads" class="muted">Checking for outputs…</span>
        </div>
        <div v-if="loadingDownloads" class="muted">Loading outputs…</div>
        <div v-else-if="!downloads.length" class="muted">No outputs yet.</div>
        <ul v-else class="space-y-2">
          <li v-for="item in downloads" :key="item.key || item.url" class="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2">
            <div>
              <div class="text-sm font-medium text-gray-900">{{ downloadFileName(item) }}</div>
              <div v-if="item.ceremony" class="text-xs text-gray-500">Ceremony: {{ ceremonyLabelFromId(item.ceremony) }}</div>
            </div>
            <a class="btn" :href="item.url" target="_blank" rel="noopener">Download</a>
          </li>
        </ul>
      </SectionCard>
    </div>

    <div class="footer-nav">
      <button class="btn" @click="goBack" :disabled="!canGoBack">Back</button>
      <button v-if="currentTab !== 'run'" class="btn primary" @click="goNext" :disabled="!canNext">{{ nextLabel }}</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ContextHeader from './ContextHeader.vue'
import SectionCard from './SectionCard.vue'

export default {
  name: 'ImportWizard',
  components: { ContextHeader, SectionCard },
  data(){
    return { file: null, uploading: false, importId: this.$route.params.id || '', currentTab: 'setup', mappingText: '', report: null, processed: null, universities: [], selectedUniversityId: '', ceremonies: [], ceremoniesLoading: false, selectedCeremonyId: '', pendingCeremonyId: '', sheetNames: [], sheets: [], selectedSheet: '', mapUI: { full_name: '', first_name: '', last_name: '', email: '' }, mapOptional: {}, showAdvanced: false, downloads: [], staged: [], meta: null, savingMapping: false, validating: false, staging: false, suggesting: false, running: false, runSteps: { save: 'idle', validate: 'idle', import: 'idle' }, notices: [], noticeTimers: {}, filterText: '', preview: { rows: [], limit: 10, mode: 'head' }, loadingPreview: false, loadingStaged: false, loadingDownloads: false, polling: { downloads: false }, pollTimers: { downloads: null }, showReportJson: false, justStaged: false, downloadingMapped: false }
  },
  watch: {
    selectedUniversityId(val){
      if (val) { this.selectedCeremonyId = ''; this.loadCeremonies() }
    }
  },
  computed: {
    isBusy(){ return !!(this.uploading || this.savingMapping || this.suggesting || this.validating || this.staging || this.running) },
    isStaged(){ const s = (this.meta && this.meta.status) || ''; return s === 'staged' || this.justStaged },
    hasMappingSaved(){
      const s = (this.meta && this.meta.status) || ''
      return ['mapping_provided', 'validated', 'validation_failed', 'staged'].includes(s)
    },
    selectedUniversity(){ return this.universities.find(u => u.id === this.selectedUniversityId) || null },
    optionalFields(){ const u = this.selectedUniversity; const arr = (u && (u.additionalFields || u.additional_fields || [])) || []; return Array.isArray(arr) ? arr : [] },
    requiredAdditionalKeys(){ try { return (this.optionalFields || []).filter(f => f && (f.primary || f.secondary)).map(f => f.name || f.key).filter(Boolean) } catch(e){ return [] } },
    selectedUniversityName(){ const u = this.selectedUniversity; return u ? `${u.name} (${u.slug})` : '' },
    selectedCeremonyLabel(){
      const c = (this.ceremonies || []).find(x => (x.id || x.ceremony_id) === this.selectedCeremonyId)
      return c ? this.formatCeremony(c) : ''
    },
    activeSheet(){
      return this.sheets.find(s => s.name === this.selectedSheet) || null
    },
    filteredHeaders(){
      if (!this.activeSheet) return []
      const q = (this.filterText || '').toLowerCase()
      const all = this.activeSheet.headers || []
      if (!q) return all
      return all.filter(h => (h||'').toLowerCase().includes(q))
    },
    statusText(){
      if (this.downloads && this.downloads.length) return 'Complete'
      const s = (this.meta && this.meta.status) || 'unknown'
      const map = { awaiting_upload: 'Awaiting Upload', mapping_provided: 'Mapping Provided', validated: 'Validated', validation_failed: 'Validation Failed', staged: 'Staged' }
      return map[s] || s
    },
    statusClass(){
      const s = (this.meta && this.meta.status) || 'unknown'
      const complete = !!(this.downloads && this.downloads.length)
      return {
        complete,
        ok: !complete && (s === 'validated' || s === 'staged'),
        warn: s === 'mapping_provided',
        err: s === 'validation_failed',
        idle: s === 'awaiting_upload' || s === 'unknown'
      }
    },
    totalStagedRows(){
      const sc = (this.meta && this.meta.staged_counts) || {}
      return Object.values(sc).reduce((a, b) => a + Number(b || 0), 0)
    },
    nextAction(){
      if (this.downloads && this.downloads.length) return 'Download outputs'
      const s = (this.meta && this.meta.status) || 'unknown'
      if (s === 'awaiting_upload') return 'Upload the Excel file'
      if (s === 'mapping_provided') return 'Import & Run'
      if (s === 'validation_failed') return 'Fix errors then Import & Run'
      if (s === 'validated' || s === 'staged') return (this.downloads && this.downloads.length) ? 'Download outputs' : 'Processing…'
      return ''
    },
    canGoConfigure(){ return !!this.importId && !this.isStaged },
    canGoPreview(){ return !!this.importId && this.hasMappingSaved },
    canGoRun(){ return !!this.importId },
    canGoBack(){ return this.currentTab !== 'setup' && !this.isStaged },
    showDownloads(){
      const s = (this.meta && this.meta.status) || ''
      return (this.downloads && this.downloads.length) || s === 'validated' || s === 'staged'
    },
    canNext(){
      if (this.currentTab === 'setup') return !!this.importId && !!this.selectedCeremonyId
      if (this.currentTab === 'configure') return !this.isStaged && this.mappingValid && !!this.selectedSheet && !!this.selectedCeremonyId
      if (this.currentTab === 'preview') return true
      if (this.currentTab === 'run') return !this.running && !this.isStaged
      return false
    },
    nextLabel(){
      if (this.currentTab === 'run') return 'Import graduates'
      return 'Next'
    },
    groupedErrors(){
      const errs = (this.report && Array.isArray(this.report.errors)) ? this.report.errors : []
      return errs.map((e, i) => ({ _k: `${e.sheet}-${e.row}-${i}` , sheet: e.sheet, row: e.row, error: e.error }))
    },
    reportJson(){
      try { return JSON.stringify(this.report, null, 2) } catch(e){ return '' }
    },
    previewExtraKeys(){
      if (!this.preview || !this.preview.rows || !this.preview.rows.length) return []
      const keys = Object.keys(this.preview.rows[0] || {})
      return keys.filter(k => !['row_index','full_name','ceremony','email','_missing_full_name','_missing_ceremony','_missing_email'].includes(k) && !k.startsWith('_missing_'))
    },
    previewHasErrors(){
      const rows = (this.preview && this.preview.rows) || []
      return rows.some(r => {
        if (!r) return false
        if (r._missing_full_name || r._missing_ceremony || r._missing_email) return true
        return (this.requiredAdditionalKeys || []).some(k => r[`_missing_${k}`])
      })
    },
    previewModeNote(){
      if (!this.preview || this.preview.mode !== 'errors') return ''
      const count = this.preview.preview_count || (this.preview.rows || []).length || 0
      const scanned = this.preview.rows_scanned ? ` in first ${this.preview.rows_scanned} rows` : ''
      return `Showing rows with missing required fields (${count} found${scanned}).`
    },
    stagedDisplay(){
      const items = Array.isArray(this.staged) ? this.staged : []
      const xlsx = items.filter(i => i && i.format === 'xlsx')
      return xlsx.length ? xlsx : items
    },
    parsedAdvancedMapping(){
      if (!this.showAdvanced) return null
      if (!this.mappingText || !this.mappingText.trim()) return null
      try { return JSON.parse(this.mappingText) } catch(e){ return null }
    },
    hasNameMapping(){ return !!(this.mapUI.full_name || (this.mapUI.first_name && this.mapUI.last_name)) },
    missingRequiredFields(){
      const hasAdvanced = this.showAdvanced && this.mappingText && this.mappingText.trim()
      if (hasAdvanced) {
        const parsed = this.parsedAdvancedMapping || {}
        return (this.requiredAdditionalKeys || []).filter(k => !parsed[k] && !this.mapOptional[k])
      }
      return (this.requiredAdditionalKeys || []).filter(k => !this.mapOptional[k])
    },
    mappingValid(){
      const hasAdvanced = this.showAdvanced && this.mappingText && this.mappingText.trim()
      if (hasAdvanced) {
        const parsed = this.parsedAdvancedMapping
        if (!parsed) return false
        const hasName = !!(parsed.full_name || (parsed.first_name && parsed.last_name) || this.hasNameMapping)
        const missingRequired = (this.requiredAdditionalKeys || []).filter(k => !parsed[k] && !this.mapOptional[k])
        return hasName && missingRequired.length === 0
      }
      return this.hasNameMapping && this.missingRequiredFields.length === 0
    },
    usingFullName(){ return !!this.mapUI.full_name },
    usingFirstLast(){ return !!(this.mapUI.first_name || this.mapUI.last_name) }
  },
  methods: {
    resetImportState(){
      this.meta = null
      this.report = null
      this.processed = null
      this.downloads = []
      this.preview = { rows: [], limit: 10, mode: 'head' }
      this.sheets = []
      this.sheetNames = []
      this.selectedSheet = ''
      this.mappingText = ''
      this.mapUI = { full_name: '', first_name: '', last_name: '', email: '' }
      this.mapOptional = {}
      this.showAdvanced = false
      this.runSteps = { save: 'idle', validate: 'idle', import: 'idle' }
      this.justStaged = false
      this.loadingDownloads = false
      this.stopPolling('downloads')
    },
    triggerFile(){ if (this.$refs.fileInput) this.$refs.fileInput.click() },
    errorExplanation(msg){
      const m = (msg||'').toLowerCase()
      if (m.includes('missing full_name')) return 'Map a Full Name column, or map First + Last in Match your columns.'
      if (m.includes('missing mapping for')) return 'Select a column mapping for this required field in Match your columns.'
      if (m.includes('missing ceremony assignment')) return 'Select a ceremony on the Setup tab.'
      if (m.includes('missing ceremony')) return 'Your mapping includes a ceremony column but values are missing. Either select a ceremony on Setup or adjust the mapping.'
      if (m.includes('multiple ceremonies')) return 'Use one ceremony per sheet or select a single ceremony on Setup.'
      return 'Review mapping and your source data on the indicated sheet and row.'
    },
    switchToFullName(){ this.mapUI.first_name = ''; this.mapUI.last_name = '' },
    switchToFirstLast(){ this.mapUI.full_name = '' },
    clearMapping(){ this.mapUI = { full_name: '', first_name: '', last_name: '', email: '' }; this.mapOptional = {} },
    pickPersistedCeremony(rows){
      if (!Array.isArray(rows) || !rows.length) return null
      return rows.find(r => r && r.status === 'mapped') || rows.find(r => r && r.status === 'staged') || rows[0]
    },
    goTab(tab){
      if (this.isStaged && (tab==='setup' || tab==='configure')) return
      if (tab==='setup' || (tab==='configure' && this.canGoConfigure) || (tab==='preview' && this.canGoPreview) || (tab==='run' && this.canGoRun)) {
        this.currentTab = tab
        if (tab==='preview') this.loadPreview()
      }
    },
    async goNext(){
      if (this.currentTab === 'setup') { this.currentTab = 'configure'; return }
      if (this.currentTab === 'configure') {
        const ok = await this.saveMapping()
        if (!ok) return
        this.currentTab = 'preview'
        await this.loadPreview()
        return
      }
      if (this.currentTab === 'preview') { this.currentTab = 'run'; return }
      if (this.currentTab === 'run') { await this.importAndRun(); return }
    },
    goBack(){
      const order = ['setup','configure','preview','run']
      const idx = order.indexOf(this.currentTab)
      if (idx > 0) this.currentTab = order[idx-1]
    },
    addNotice(text, type='info'){
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`
      this.notices.push({ id, text, type })
      const timeoutMs = type === 'error' ? 10000 : 6000
      const timer = setTimeout(() => this.dismissNotice(id), timeoutMs)
      this.$set(this.noticeTimers, id, timer)
    },
    dismissNotice(id){
      const idx = this.notices.findIndex(n => n.id === id)
      if (idx !== -1) this.notices.splice(idx, 1)
      const timer = this.noticeTimers && this.noticeTimers[id]
      if (timer) clearTimeout(timer)
      if (this.noticeTimers && id in this.noticeTimers) this.$delete(this.noticeTimers, id)
    },
    fmt(d){ try { return new Date(d).toLocaleString() } catch (e) { return '' } },
    async loadUniversities(){
      const base = process.env.VUE_APP_BASE_URL
      try {
        const r = await axios.get(`${base}/universities`)
        this.universities = r.data || []
      } catch(e) { console.error(e) }
    },
    async onFile(e){
      this.file = e.target.files && e.target.files[0]
      if (!this.file) return
      if (!this.selectedUniversityId){
        // University is selected on Imports page; guard here just in case
        this.addNotice('Select a university before uploading.', 'error')
        return
      }
      this.resetImportState()
      const base = process.env.VUE_APP_BASE_URL
      this.uploading = true
      try {
        const r = await axios.post(`${base}/imports`, { filename: this.file.name, university_id: this.selectedUniversityId })
        const { presigned_put_url, import_id } = r.data
        this.importId = import_id
        await fetch(presigned_put_url, { method: 'PUT', body: this.file, headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' } })
        await this.refreshImport()
        await this.loadCeremonies()
        await this.loadSheets()
        // Keep success lightweight and neutral to avoid ceremony-specific guidance
        this.addNotice('Upload complete.', 'success')
        // Keep the current route to avoid remount flicker
      } catch(e){ console.error(e) }
      finally { this.uploading = false }
    },
    async loadSheets(){
      const base = process.env.VUE_APP_BASE_URL
      try {
        const r = await axios.get(`${base}/imports/${this.importId}/sheets`)
        this.sheets = r.data && r.data.sheets || []
        this.sheetNames = this.sheets.map(s => s.name)
        if (this.sheets.length && !this.selectedSheet) {
          this.selectedSheet = this.sheets[0].name
        }
        // Only auto-suggest if no mapping exists yet and no UI selection present
        const noMappingYet = !this.mappingText && !this.mapUI.full_name && !this.mapUI.first_name && !this.mapUI.last_name
        if (noMappingYet) {
          try { await this.suggest() } catch (e) { /* non-blocking */ }
        }
      } catch(e){ console.error(e) }
    },
    onSheetChange(){
      // Reset mapping UI when sheet changes
      this.mapUI = { full_name: '', first_name: '', last_name: '', email: '' }
      this.mapOptional = {}
      this.loadPreview()
    },
    async suggest(){
      const base = process.env.VUE_APP_BASE_URL
      this.suggesting = true
      try {
        const r = await axios.get(`${base}/imports/${this.importId}/suggest-mapping`)
        const suggestions = r.data && r.data.suggestions || {}
        // choose selected sheet suggestion or first available
        const first = (this.selectedSheet && suggestions[this.selectedSheet]) ? suggestions[this.selectedSheet] : (Object.values(suggestions)[0] || {})
      const withSelected = { selected_sheet: this.selectedSheet || (this.sheetNames[0] || ''), ...first }
      this.mappingText = JSON.stringify(withSelected, null, 2)
      this.sheetNames = Object.keys(suggestions)
        // Reflect into visual UI
        const pickFrom = (spec) => { if (!spec) return ''; if (typeof spec === 'string') return spec; if (Array.isArray(spec.from) && spec.from.length) return spec.from[0]; return '' }
        this.mapUI.full_name = pickFrom(withSelected.full_name)
        this.mapUI.first_name = pickFrom(withSelected.first_name)
        this.mapUI.last_name = pickFrom(withSelected.last_name)
        this.mapUI.email = pickFrom(withSelected.email)
        this.addNotice('We matched columns automatically — review below', 'info')
      } finally { this.suggesting = false }
    },
    async loadStaged(){
      if (!this.importId) return
      const base = process.env.VUE_APP_BASE_URL
      this.loadingStaged = true
      try {
        const r = await axios.get(`${base}/imports/${this.importId}/staged`)
        this.staged = (r.data && r.data.staged) || []
      } catch(e){ console.error(e) }
      finally { this.loadingStaged = false }
    },
    async loadDownloads({ silent = false } = {}){
      if (!this.importId) return
      const base = process.env.VUE_APP_BASE_URL
      if (!silent) this.loadingDownloads = true
      try {
        const r = await axios.get(`${base}/imports/${this.importId}/download`)
        this.downloads = (r.data && r.data.downloads) || []
      } catch(e){ console.error(e) }
      finally { if (!silent) this.loadingDownloads = false }
    },
    async downloadMapped(){
      if (!this.importId) return
      const base = process.env.VUE_APP_BASE_URL
      this.downloadingMapped = true
      try {
        const r = await axios.get(`${base}/imports/${this.importId}/mapped`)
        const url = r.data && r.data.url
        if (url) {
          window.open(url, '_blank')
        } else {
          this.addNotice('Mapped file not available yet.', 'error')
        }
      } catch(e){
        console.error(e)
        this.addNotice('Unable to download mapped file.', 'error')
      } finally {
        this.downloadingMapped = false
      }
    },
    exportErrorsCsv(){
      if (!this.report || !Array.isArray(this.report.errors)) return
      const rows = this.report.errors
      const headers = ['sheet','row','error']
      const lines = [headers.join(',')]
      rows.forEach(r => {
        const vals = headers.map(h => {
          const v = (r && r[h] !== undefined) ? String(r[h]) : ''
          const esc = '"' + v.replace(/"/g, '""') + '"'
          return esc
        })
        lines.push(vals.join(','))
      })
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `import_${this.importId}_validation_errors.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
    async saveMapping(){
      const base = process.env.VUE_APP_BASE_URL
      let mapping = {}
      // Only parse JSON if the user toggled advanced mode and provided content
      if (this.showAdvanced && this.mappingText && this.mappingText.trim()) {
        try { mapping = JSON.parse(this.mappingText) } catch(e){ this.addNotice('Mapping must be valid JSON', 'error'); return false }
      }
      // Merge visual mapping
      if (this.selectedSheet) mapping.selected_sheet = this.selectedSheet
      const mUpdates = {}
      if (this.mapUI.full_name) mUpdates.full_name = { from: [this.mapUI.full_name], normalize: ['strip', 'collapse_whitespace', 'titlecase'] }
      if (this.mapUI.first_name) mUpdates.first_name = { from: [this.mapUI.first_name], normalize: ['strip', 'collapse_whitespace', 'titlecase'] }
      if (this.mapUI.last_name) mUpdates.last_name = { from: [this.mapUI.last_name], normalize: ['strip', 'collapse_whitespace', 'titlecase'] }
      if (this.mapUI.email) mUpdates.email = { from: [this.mapUI.email], normalize: ['strip', 'lower'] }
      // Optional fields mapping from university.additionalFields
      Object.keys(this.mapOptional || {}).forEach((k) => {
        const col = this.mapOptional[k]
        if (col) mUpdates[k] = { from: [col], normalize: ['strip'] }
      })
      mapping = { ...mapping, ...mUpdates }
      // Embed required additional fields (from university optional fields flags)
      if ((this.requiredAdditionalKeys || []).length) mapping._required_fields = this.requiredAdditionalKeys
      // Minimal validation: need full_name or first+last, and a ceremony mapping/fallback
      const missing = []
      const hasName = !!(mapping.full_name || (mapping.first_name && mapping.last_name))
      const missingRequired = (this.requiredAdditionalKeys || []).filter(k => !mapping[k])
      if (!hasName) missing.push('Full Name or First + Last')
      if (!this.selectedSheet) missing.push('Excel sheet')
      if (!this.selectedCeremonyId) missing.push('Ceremony')
      if (missingRequired.length) missing.push(`Required fields (${missingRequired.join(', ')})`)
      if (missing.length) {
        this.addNotice(`Complete required fields: ${missing.join('; ')}`, 'error')
        return false
      }
      this.savingMapping = true
      try {
        await axios.post(`${base}/imports/${this.importId}/mappings`, { mapping, ceremony_id: this.selectedCeremonyId })
        await this.refreshImport()
        this.addNotice('Mapping saved', 'success')
        await this.loadPreview()
      } finally { this.savingMapping = false }
      // Optionally reflect back to JSON view
      if (this.showAdvanced) this.mappingText = JSON.stringify(mapping, null, 2)
      return true
    },
    async loadCeremonies(){
      const base = process.env.VUE_APP_BASE_URL
      this.ceremoniesLoading = true
      try {
        const r = await axios.get(`${base}/universities/${this.selectedUniversityId}/ceremonies`)
        this.ceremonies = r.data || []
        if (this.pendingCeremonyId) {
          const match = (this.ceremonies || []).find(c => (c.id || c.ceremony_id) === this.pendingCeremonyId)
          if (match) {
            this.selectedCeremonyId = match.id || match.ceremony_id
            this.pendingCeremonyId = ''
          } else if (this.ceremonies.length) {
            this.pendingCeremonyId = ''
          }
        }
        if (this.ceremonies.length === 1 && !this.selectedCeremonyId) {
          const one = this.ceremonies[0]
          this.selectedCeremonyId = (one && (one.id || one.ceremony_id)) || ''
        }
      } catch(e){ console.error(e) }
      finally { this.ceremoniesLoading = false }
    },
    formatCeremony(c){
      try { return new Date(c.ceremony_datetime || c.datetime).toLocaleString() } catch { return c.ceremony_id }
    },
    ceremonyLabelFromId(id){
      if (!id) return ''
      const match = (this.ceremonies || []).find(c => (c.id || c.ceremony_id) === id)
      return match ? this.formatCeremony(match) : id
    },
    formatBytes(size){
      const val = Number(size || 0)
      if (!val) return ''
      if (val < 1024) return `${val} B`
      if (val < 1024 * 1024) return `${(val / 1024).toFixed(1)} KB`
      if (val < 1024 * 1024 * 1024) return `${(val / 1024 / 1024).toFixed(1)} MB`
      return `${(val / 1024 / 1024 / 1024).toFixed(1)} GB`
    },
    stagedLabel(item){
      const ceremony = item && item.ceremony ? item.ceremony : 'Ceremony'
      const format = (item && item.format ? item.format : 'file').toUpperCase()
      return `${ceremony} — ${format}`
    },
    downloadFileName(item){
      if (!item) return 'Output file'
      const key = item.file_name || item.key || ''
      if (!key) return 'Output file'
      const parts = key.split('/')
      return parts[parts.length - 1] || key
    },
    async refreshImport(){
      const base = process.env.VUE_APP_BASE_URL
      try {
        const r = await axios.get(`${base}/imports/${this.importId}`)
        const data = r.data || {}
        this.meta = data.meta || null
        if (this.meta && this.meta.status === 'staged') {
          this.justStaged = true
          await this.loadStaged()
        }
        if (data.report) this.report = data.report
        // ceremonies list is for downloads stage only now
        // Ensure university is selected when viewing an existing import
        const uniId = data.meta && data.meta.university_id
        if (uniId && !this.selectedUniversityId) {
          this.selectedUniversityId = uniId
          // Load ceremonies for this university for assignments
          await this.loadCeremonies()
        }
        // If a ceremony was previously selected for this import, reflect it (but do not override a current user choice)
        if (!this.selectedCeremonyId && Array.isArray(data.ceremonies) && data.ceremonies.length) {
          const picked = this.pickPersistedCeremony(data.ceremonies)
          if (picked && picked.ceremony_id) this.selectedCeremonyId = picked.ceremony_id
        }
        // Load existing mapping (if any) and reflect into visual UI
        if (data.mapping) {
          const baseMapping = data.mapping
          try { this.mappingText = JSON.stringify(baseMapping, null, 2) } catch (e) { /* ignore stringify */ }
          if (baseMapping.selected_sheet) this.selectedSheet = baseMapping.selected_sheet
          // Apply sheet-specific overrides if present
          let eff = { ...baseMapping }
          if (baseMapping.sheets && this.selectedSheet && baseMapping.sheets[this.selectedSheet]) {
            eff = { ...eff, ...baseMapping.sheets[this.selectedSheet] }
          }
          // Extract columns for visual mapping
          const pickFrom = (spec) => {
            if (!spec) return ''
            if (typeof spec === 'string') return spec
            if (Array.isArray(spec.from) && spec.from.length) return spec.from[0]
            return ''
          }
          // Handle concat first/last to visual fields if present
          const pickConcat = (spec) => {
            if (!spec || !Array.isArray(spec.concat)) return { first: '', last: '' }
            const cols = spec.concat.filter((p) => p && p.col).map((p) => p.col)
            if (cols.length >= 2) return { first: cols[0] || '', last: cols[1] || '' }
            return { first: '', last: '' }
          }
          const full = pickFrom(eff.full_name)
          let first = pickFrom(eff.first_name)
          let last = pickFrom(eff.last_name)
          if (!first && !last && eff.full_name && typeof eff.full_name === 'object' && Array.isArray(eff.full_name.concat)) {
            const c = pickConcat(eff.full_name)
            first = c.first
            last = c.last
          }
          this.mapUI.full_name = full || ''
          this.mapUI.first_name = first || ''
          this.mapUI.last_name = last || ''
          this.mapUI.email = pickFrom(eff.email)
          // Reflect optional fields (anything not in the known meta/required keys)
          const known = new Set(['version','sheets','selected_sheet','selected_sheets','full_name','first_name','last_name','ceremony','registration_id','email'])
          Object.keys(eff || {}).forEach((k) => {
            if (!known.has(k) && typeof eff[k] !== 'undefined') {
              const v = pickFrom(eff[k])
              if (v) this.$set(this.mapOptional, k, v)
            }
          })
        }
        const status = (this.meta && this.meta.status) || ''
        if (status === 'validated' || status === 'staged') {
          await this.loadDownloads()
          if (!this.downloads.length && !this.polling.downloads) this.startPolling('downloads')
        } else if (this.polling.downloads) {
          this.stopPolling('downloads')
        }
      } catch(e){ console.error(e) }
    },
    async validate(){
      const base = process.env.VUE_APP_BASE_URL
      this.validating = true
      try {
        const r = await axios.post(`${base}/imports/${this.importId}/validate`, {})
        this.report = r.data
        await this.refreshImport()
      } finally { this.validating = false }
    },
    async process(){
      const base = process.env.VUE_APP_BASE_URL
      this.staging = true
      try {
        const r = await axios.post(`${base}/imports/${this.importId}/process`, {})
        this.processed = r.data
        this.justStaged = true
        this.addNotice('Import completed. We’ll be in touch when outputs are ready (typically 24–48 hours after the ceremony).', 'success')
      } finally { this.staging = false }
    },
    async importAndRun(){
      // Save mapping, then validate, then process automatically
      this.runSteps = { save: 'idle', validate: 'idle', import: 'idle' }
      this.running = true
      try {
        this.runSteps.save = 'in_progress'
        const saved = await this.saveMapping()
        if (!saved) {
          this.runSteps.save = 'error'
          return
        }
        this.runSteps.save = 'done'
        this.runSteps.validate = 'in_progress'
        await this.validate()
        if (this.report && (this.report.error_count || 0) > 0) {
          this.runSteps.validate = 'error'
          this.addNotice('Validation found issues. Fix mapping and try again.', 'error')
          return
        }
        this.runSteps.validate = 'done'
        this.runSteps.import = 'in_progress'
        await this.process()
        this.runSteps.import = 'done'
        await this.refreshImport()
      } finally {
        this.running = false
      }
    },
    async loadPreview(){
      if (!this.importId || !this.selectedSheet) return
      this.loadingPreview = true
      const base = process.env.VUE_APP_BASE_URL
      try {
        const r = await axios.get(`${base}/imports/${this.importId}/preview`, { params: { sheet: this.selectedSheet, limit: 10 } })
        const data = r.data || {}
        this.preview = { rows: [], limit: 10, mode: 'head', ...data }
      } catch(e){ /* silent */ }
      finally { this.loadingPreview = false }
    },
    startPolling(kind){
      const maxMs = 120000
      const interval = 5000
      if (this.pollTimers[kind]) clearInterval(this.pollTimers[kind])
      const started = Date.now()
      this.polling[kind] = true
      this.pollTimers[kind] = setInterval(async () => {
        if (Date.now() - started > maxMs) { this.stopPolling(kind); return }
        if (kind === 'staged') await this.loadStaged()
        if (kind === 'downloads') await this.loadDownloads({ silent: true })
        if (kind === 'downloads' && this.downloads && this.downloads.length) this.stopPolling('downloads')
      }, interval)
    },
    stopPolling(kind){
      if (this.pollTimers[kind]) { clearInterval(this.pollTimers[kind]); this.pollTimers[kind] = null }
      this.polling[kind] = false
    },
    toggleReportJson(){ this.showReportJson = !this.showReportJson }
  },
  beforeDestroy(){
    try { Object.keys(this.pollTimers || {}).forEach(k => this.stopPolling(k)) } catch(e) { /* noop */ }
    try {
      Object.values(this.noticeTimers || {}).forEach(t => clearTimeout(t))
      this.noticeTimers = {}
    } catch(e) { /* noop */ }
  },
  beforeRouteLeave(to, from, next){
    try { Object.keys(this.pollTimers || {}).forEach(k => this.stopPolling(k)) } catch(e) { /* noop */ }
    next()
  },
  async created(){
    // Accept university scope from route query if provided
    const qUni = (this.$route.query && (this.$route.query.university_id || this.$route.query.universityId)) || ''
    const qCeremony = (this.$route.query && (this.$route.query.ceremony_id || this.$route.query.ceremonyId)) || ''
    const qTab = (this.$route.query && (this.$route.query.tab || this.$route.query.currentTab)) || ''
    const qTabLower = String(qTab || '').toLowerCase()
    const preferRun = qTabLower === 'run' || qTabLower === 'outputs'
    if (preferRun) this.currentTab = 'run'
    if (qUni) this.selectedUniversityId = qUni
    if (qCeremony) this.pendingCeremonyId = qCeremony
    await this.loadUniversities()
    if (!this.selectedUniversityId && this.universities.length === 1){
      this.selectedUniversityId = this.universities[0].id
    }
    if (this.selectedUniversityId) {
      await this.loadCeremonies()
    }
    if (this.importId){
      await this.refreshImport()
      if (!this.downloads.length) await this.loadDownloads({ silent: true })
      const hasDownloads = !!(this.downloads && this.downloads.length)
      const wantsRun = preferRun || hasDownloads
      if (!wantsRun) await this.loadSheets()
      // Decide initial tab based on status
      const status = (this.meta && this.meta.status) || 'awaiting_upload'
      if (wantsRun) {
        this.currentTab = 'run'
      } else {
        let nextTab = 'configure'
        if (status === 'awaiting_upload') nextTab = 'setup'
        else if (status === 'staged') nextTab = 'run'
        else if (status === 'validated' || status === 'mapping_provided') nextTab = 'preview'
        else if (status === 'validation_failed') nextTab = 'configure'
        this.currentTab = nextTab
      }
      if (this.currentTab === 'preview' && this.selectedSheet) await this.loadPreview()
    }
  }
}
</script>

<style scoped>
.page { max-width: 1100px; margin: 0 auto; padding: 12px 16px; }
.topbar { display: flex; align-items: center; gap: 12px; }
.notices { margin: 8px 0; }
.notice { padding: 8px 10px; border-radius: 6px; border: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.notice.info { background: #eff6ff; color: #1e3a8a; }
.notice.success { background: #ecfdf5; color: #065f46; }
.notice.error { background: #fef2f2; color: #991b1b; }
.notice .close { background: transparent; border: none; cursor: pointer; font-size: 16px; line-height: 1; }
.card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; margin: 12px 0; }
.row { margin: 8px 0; }
.btn { padding: 6px 10px; border: 1px solid #e5e7eb; background: #fff; border-radius: 6px; cursor: pointer; }
.btn.primary { background: #111827; color: #fff; border-color: #111827; }
.json { width: 100%; min-height: 180px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; font-size: 12px; }
select { padding: 6px 8px; border: 1px solid #e5e7eb; border-radius: 6px; }
.grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
.col label { display: block; font-weight: 600; margin-bottom: 4px; }
.preview { overflow: auto; margin: 8px 0; }
.table.small th, .table.small td { padding: 4px 6px; font-size: 12px; }
.section-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.status { background: #fafafa; position: sticky; top: 0; z-index: 10; }
.status .status-row { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; }
.status .status-row .next { margin-left: auto; color: #374151; }
.status .metrics { display: flex; gap: 18px; flex-wrap: wrap; margin-top: 8px; color: #374151; }
.status .metrics .metric { display: flex; gap: 6px; align-items: baseline; }
.status .metrics .metric .label { color: #6b7280; }
.status .metrics .metric .value { font-weight: 600; }
.status .metrics .metric .value.bad { color: #991b1b; }
.status .metrics .metric .sub { color: #6b7280; }
.pill { display: inline-block; padding: 2px 8px; border-radius: 9999px; font-size: 12px; font-weight: 600; border: 1px solid transparent; }
.pill.ok { background: #ecfdf5; color: #065f46; border-color: #a7f3d0; }
.pill.complete { background: #ecfeff; color: #0e7490; border-color: #a5f3fc; }
.pill.warn { background: #eff6ff; color: #1e40af; border-color: #bfdbfe; }
.pill.err { background: #fef2f2; color: #991b1b; border-color: #fecaca; }
.pill.idle { background: #f3f4f6; color: #374151; border-color: #e5e7eb; }
.tabs { display: flex; gap: 8px; margin-top: 8px; }
.tabs .tab { padding: 6px 10px; border-radius: 9999px; border: 1px solid #e5e7eb; background: #fff; cursor: pointer; }
.tabs .tab.active { background: #eef2ff; color: #3730a3; border-color: #c7d2fe; }
.tabs .tab.disabled { opacity: 0.5; pointer-events: none; }
.inline-status { display: inline-flex; gap: 6px; align-items: center; color: #374151; }
.spinner { display: inline-block; border: 2px solid #e5e7eb; border-top-color: #6b7280; border-radius: 50%; animation: spin 0.9s linear infinite; }
.spinner.sm { width: 14px; height: 14px; }
.spinner.lg { width: 24px; height: 24px; border-width: 3px; }
@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }
.footer-nav { display: flex; justify-content: space-between; align-items: center; position: sticky; bottom: 0; background: #fff; padding: 8px 0; gap: 8px; }
.validation .summary { display: flex; gap: 10px; margin: 8px 0; }
.validation .chip { background: #f3f4f6; border-radius: 9999px; padding: 2px 8px; }
.validation .chip.bad { background: #fef2f2; color: #991b1b; }
.muted { color: #6b7280; }
.bad { color: #991b1b; font-weight: 600; }
.ceremony-row { display: flex; gap: 10px; align-items: center; }
.link { text-decoration: underline; cursor: pointer; }
.mode-row { display: flex; gap: 8px; align-items: center; }
.config-grid { display: grid; grid-template-columns: minmax(0, 1fr) 420px; gap: 16px; align-items: start; }
.map-grid { display: grid; grid-template-columns: 1fr; gap: 8px; }
.map-panel select { width: 100%; }
@media (max-width: 980px) {
  .config-grid { grid-template-columns: 1fr; }
}
.map-panel { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; background: #fafafa; }
.map-panel .helper { color: #374151; margin-bottom: 6px; font-size: 13px; }
.ok-chip { background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; border-radius: 9999px; padding: 2px 8px; font-size: 12px; font-weight: 600; }
.bad-chip { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; border-radius: 9999px; padding: 2px 8px; font-size: 12px; font-weight: 600; }
</style>
